import { useEffect, useRef, useState } from "react";
import { useUserData } from "context/OAuth/AuthContext";

import imageStep1 from "@assets/images/mindfullImages/Step1.png";
import imageStep2 from "@assets/images/mindfullImages/Step2.png";
import imageStep3 from "@assets/images/mindfullImages/Step3.png";
import imageStep4 from "@assets/images/mindfullImages/Step4.png";
import imageStep5 from "@assets/images/mindfullImages/Step5.png";
import imageStep6 from "@assets/images/mindfullImages/Step6.png";
import imageStep7 from "@assets/images/mindfullImages/Step7.png";
import imageStep8 from "@assets/images/mindfullImages/Step8.png";
import {
  initialSleepData,
  Step1,
  Step2,
  Step3,
  Step4,
  Step5,
  Step6,
  Step7,
  Step8,
} from "data/dropdowndData/MindfulnessDropdown";
import StepsFrom from "../../features/mindfulness/StepsForm";
import SleepCoachStepper from "../../features/mindfulness/SleepCoachStepper";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { NavigationPrompt } from "@utils/NavigationBlocker";
import { useTranslation } from "react-i18next";
import ActualDietPlanLoader from "@components/ui/Loaders/ActualDietPlanLoader";
import {

  createMindfulnessPlan,
  fetchMindfulnessSteps,
  submitMindfulnessEntries,
} from "api/mindfulness";
const Mindfullness = () => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { subscription } = useUserData();
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const [firstLoad, setFirstLoad] = useState(true);
  const isMounted = useRef(false);
  const { t } = useTranslation();

  // State and handlers that need to be shared with child components
  const [selectedTags, setSelectedTags] = useState(initialSleepData);

  const steps = [
    {
      title: t("mindfulnessCoachPreferences.step1Label"),
      options: Step1,
      src: imageStep1,
    },
    {
      title: t("mindfulnessCoachPreferences.step2Label"),
      options: Step2,
      src: imageStep2,
    },
    {
      title: t("mindfulnessCoachPreferences.step3Label"),
      options: Step3,
      src: imageStep3,
    },
    {
      title: t("mindfulnessCoachPreferences.step4Label"),
      options: Step4,
      src: imageStep4,
    },
    {
      title: t("mindfulnessCoachPreferences.step5Label"),
      options: Step5,
      src: imageStep5,
    },
    {
      title: t("mindfulnessCoachPreferences.step6Label"),
      options: Step6,
      src: imageStep6,
    },
    {
      title: t("mindfulnessCoachPreferences.step7Label"),
      options: Step7,
      src: imageStep7,
    },
    {
      title: t("mindfulnessCoachPreferences.step8Label"),
      options: Step8,
      src: imageStep8,
    },
  ];

  const validateInput = (key, value, label) => {
    const fieldLabel = t(`firstFormSteps.${key}.label`);
    if (key !== "dailyCalorieAdjustment") {
      // const maxLength = InputFieldValidation[key];

      // if (maxLength && value.toString().length > maxLength) {
      //   return t("validation.maxLength", { maxLength }); // "Maximum {maxLength} characters allowed"
      // }

      if (!value) {
        return t("validation.required", { field: fieldLabel }); // "{field} is required"
      }
      if (value < 1) {
        return t("validation.greaterThanZero"); // "Value must be greater than 0."
      }
      if (isNaN(value)) {
        return t("validation.validNumber"); // "Please enter a valid number"
      }
      if (value < 0) {
        return t("validation.nonNegative"); // "Value cannot be negative"
      }

      return null;
    }
  };

  const validateStep = () => {
    const currentOptions = steps[currentStep]?.options || [];
    let isValid = true;
    const newErrors = {};

    for (const field of currentOptions) {
      const value = selectedTags[field.key];
      const fieldLabel = t(`mindfulnessCoachPreferences.fields.${field.key}`);

      // Validate multi-select
      if (field.type === "multi-select") {
        if (!Array.isArray(value) || value.length === 0) {
          newErrors[field.key] = t("validation.required", {
            field: fieldLabel,
          });

          isValid = false;
        }
      } else if (field.type === "select") {
        if (!value || value === "") {
          newErrors[field.key] = t("validation.required", {
            field: fieldLabel,
          });
          isValid = false;
        }
      } else if (field.type === "numeric") {
        const inputError = validateInput(field.key, value, fieldLabel, t);
        if (inputError) {
          newErrors[field.key] = inputError;
          isValid = false;
        }
      } else {
        if (!value || value === "") {
          newErrors[field.key] = t("validation.required", {
            field: fieldLabel,
          });
          isValid = false;
        }
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await submitMindfulnessEntries(selectedTags);
      await createMindfulnessPlan();
      if (!isMounted.current) return;
      toast.info(t("toast.informationSubmit"));
      navigate("/mindfulness-dashboard", {
        state: {
          mindfulness: true,
        },
      });
    } catch (error) {
      if (!isMounted.current) return;
      console.log("Error submitting details", error);
      toast.error(t("toast.failedSubmit"));
    } finally {
      setLoading(false);
    }
  };

  const fetchStepsData = async () => {
   if (subscription?.status === "active" || subscription?.status === "pending-cancel") {
      try {
        const { data, plan, loading = false } = await fetchMindfulnessSteps();
        if (!isMounted.current) return;
        if ((data && plan) || (data && !plan && loading)) {
          navigate("/mindfulness-dashboard", {
            state: {
              mindfulness: true,
            },
          });
        } else if (data && !plan && !loading) {
          setSelectedTags(data);
          setFirstLoad(false);
        } else {
          setFirstLoad(false);
        }
      } catch (error) {
        if (!isMounted.current) return;
        console.error(
          "Error fetching Data:",
          error.response ? error.response.data : error.message
        );
        setFirstLoad(false);
      }
    } else {
      navigate("/renew-subscription");
    }
  };

  useEffect(() => {
    fetchStepsData();
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  const handleMultiSelectChange = (key, values) => {
    setSelectedTags((prev) => ({ ...prev, [key]: values }));

    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (!values || values.length === 0) {
        updatedErrors[key] = true;
      } else {
        delete updatedErrors[key];
      }
      return updatedErrors;
    });
  };

  const handleSelecChange = (key, value) => {
    setSelectedTags((prev) => ({ ...prev, [key]: value }));

    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (!value || value === "") {
        updatedErrors[key] = true;
      } else {
        delete updatedErrors[key];
      }
      return updatedErrors;
    });
  };

  const removeTag = (key, tag) => {
    const updated = selectedTags[key].filter((t) => t !== tag);

    if (updated.length === 0) {
      setErrors((prev) => ({ ...prev, [key]: true }));
    }

    setSelectedTags((prev) => ({
      ...prev,
      [key]: updated,
    }));
  };

  return (
    <div className={`min-h-[100%]`}>
      <NavigationPrompt when={loading} />
      {firstLoad ? (
        <ActualDietPlanLoader mindfulness={true} />
      ) : (
        <div className="w-full min-h-[100%]  bg-white rounded-lg">
          <div className="px-3 md:px-[35px] py-[34px] mx-auto bg-white rounded-lg mb-6 w-full max-w-[90%] lg:max-w-[900px] xl:max-w-[1128px] ">
            <SleepCoachStepper currentStep={currentStep} steps={steps} />

            <div className="mb-0 md:mb-6 px-2 md:p-0">
              <StepsFrom
                currentStep={currentStep}
                steps={steps}
                selectedTags={selectedTags}
                setSelectedTags={setSelectedTags}
                errors={errors}
                setErrors={setErrors}
                validateStep={validateStep}
                setCurrentStep={setCurrentStep}
                loading={loading}
                handleSubmit={handleSubmit}
                isAdviceLoading={false}
                handleSelecChange={handleSelecChange}
                handleInputChange={handleSelecChange}
                removeTag={removeTag}
                handleMultiSelectChange={handleMultiSelectChange}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mindfullness;
